import React, { useState, useEffect, useMemo } from 'react';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { SocketProvider } from './sockets/SocketContext';
import { AdminSocketProvider } from './sockets/AdminSocketContext';
import { useAuthSession } from './hooks/useAuthSession';

// components
import BodyContainer from './components/main/BodyContainer';
import AuthModal from './components/AuthModal';
import PendingActivationPage from './components/PendingActivationPage';

function ProvApp({ setToken, token }) {
  const { enqueueSnackbar } = useSnackbar();
  const {
    isAuthenticated,
    isActive,
    logout: handleLogout,
    refreshActivation,
  } = useAuthSession(token, setToken);
  const username = useSelector((state) => state.interface.user?.username);
  const displayUsername = useMemo(() => {
    if (username) return username;
    if (!token) return null;
    try {
      return jwtDecode(token).username;
    } catch {
      return null;
    }
  }, [username, token]);

  const handleCloseAuth = () => {
    if (!localStorage.getItem('token')) {
      return;
    }
  };

  const handleActivated = (newToken) => {
    refreshActivation(newToken);
  };

  return (
    <div className="header-main wrapper-window app-root-shell">
      {!isAuthenticated && (
        <AuthModal open={!isAuthenticated} onClose={handleCloseAuth} onAuthSuccess={setToken} />
      )}
      {isAuthenticated && !isActive && (
        <PendingActivationPage
          username={displayUsername}
          onActivated={handleActivated}
          onLogout={handleLogout}
        />
      )}
      {isAuthenticated && isActive && (
        <BodyContainer addSnackbar={enqueueSnackbar} handleLogout={handleLogout} />
      )}
    </div>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const isActive = useMemo(() => {
    if (!token) return false;
    try {
      const decoded = jwtDecode(token);
      return decoded.is_active !== false;
    } catch {
      return false;
    }
  }, [token]);

  const activeToken = token && isActive ? token : null;

  useEffect(() => {
    const onUnauthorized = () => {
      localStorage.removeItem('token');
      setToken(null);
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, []);

  return (
    <SocketProvider token={activeToken}>
      <AdminSocketProvider token={activeToken}>
        <SnackbarProvider maxSnack={3}>
          <ProvApp setToken={setToken} token={token} />
        </SnackbarProvider>
      </AdminSocketProvider>
    </SocketProvider>
  );
}

export default App;
